// app/product-brands/[slug]/BrandProductsClient.tsx
'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { fetchBrandProductsWithTags, BrandProductWithTags } from '@/lib/woocommerceFetchBrandTags';
import { Pagination } from './Pagination';
import AddToCartButtonInList from '@/components/add_to_cart_popup/AddToCartButtonInList';
import { MiniCartProvider } from '@/app/context/MiniCartContext';

const parsePrice = (price: string): number => {
    const cleanedPrice = price.replace(/[^\d.]/g, '');
    const num = parseFloat(cleanedPrice);
    return isNaN(num) ? 0 : num;
};

const formatNumber = (num: number) => num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

interface BrandProductsClientProps {
    initialProducts: BrandProductWithTags[];
    initialTotal: number;
    initialTotalPages: number;
    currentPage: number;
    perPage: number;
    brandSlug: string;
    tagFilter: string;
}

export function BrandProductsClient({
    initialProducts,
    initialTotal,
    initialTotalPages,
    currentPage,
    perPage,
    brandSlug,
    tagFilter,
}: BrandProductsClientProps) {
    const [products, setProducts] = useState(initialProducts);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentLoadedPage, setCurrentLoadedPage] = useState(currentPage);
    const searchParams = useSearchParams();
    const router = useRouter();

    const [isLoadMore, setIsLoadMore] = useState(false);
    const [previousTag, setPreviousTag] = useState(searchParams.get('tag') || '');

    useEffect(() => {
        const currentTag = searchParams.get('tag') || '';
        const tagChanged = previousTag !== currentTag;

        if (!isLoadMore) {
            setProducts(initialProducts);
            setCurrentLoadedPage(currentPage);

            // Скроллим только если изменился тег (фильтр)
            if (tagChanged && previousTag !== null) {
                setTimeout(() => {
                    const isMobile = window.innerWidth < 768;
                    const targetId = isMobile ? 'acnhor-page-point' : 'product-brand-title';
                    const element = document.getElementById(targetId);

                    if (element) {
                        const offset = isMobile ? 70 : 20;
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - offset;

                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        }

        setIsLoadMore(false);
        setPreviousTag(currentTag);
    }, [searchParams.get('page'), searchParams.get('tag'), initialProducts, currentPage, previousTag]);

    const loadMore = async () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        setIsLoadMore(true);

        try {
            const nextPage = currentLoadedPage + 1;
            const data = await fetchBrandProductsWithTags(brandSlug, nextPage, perPage, tagFilter);
            setProducts((prevProducts) => [...prevProducts, ...data.products]);
            setCurrentLoadedPage(nextPage);

            const params = new URLSearchParams(searchParams.toString());
            params.set('page', nextPage.toString());
            if (tagFilter) {
                params.set('tag', tagFilter);
            }
            router.push(`/product-brands/${brandSlug}?${params.toString()}`, { scroll: false });
        } catch (error) {
            console.error('Error loading more brand products:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    return (
        <div className="products_side">
            <p className="brandsCount my-[10px]">
                Товаров: <strong>{initialTotal}</strong>
            </p>

            <div className="shop_page_prod_grid">
                {products.map((p) => {
                    const priceNumber = parsePrice(p.price || '0');
                    const formattedPrice = priceNumber.toLocaleString('ru-RU') + ' сӯм';
                    const isInStock = p.stockStatus === 'IN_STOCK' || (p.stockQuantity && p.stockQuantity > 0);
                    const productClasses = !isInStock ? 'product_item relative grayscale opacity-80' : 'product_item relative';

                    // Вычисляем процент скидки
                    const regularPriceInSum = p.price
                        ? parseFloat(p.price.replace(/[^\d]/g, ''))
                        : 0;

                    const oldPriceInSum = p.customSalePrice || 0;

                    const discountPercent =
                        isInStock &&
                            p.hasCustomDiscount &&
                            oldPriceInSum > 0 &&
                            regularPriceInSum > 0 &&
                            oldPriceInSum > regularPriceInSum
                            ? Math.round(((oldPriceInSum - regularPriceInSum) / oldPriceInSum) * 100)
                            : 0;

                    return (
                        <div className={productClasses} key={p.id}>
                            <Link href={`/product/${p.slug}`}>
                                <Image
                                    className="product_item__image"
                                    src={p.image?.sourceUrl || '/images/products/default.jpg'}
                                    alt={p.image?.altText || p.name}
                                    width={200}
                                    height={200}
                                />
                            </Link>

                            {/* Бейдж скидки */}
                            {discountPercent > 0 && (
                                <div
                                    className="absolute top-0 left-0 bg-[#ff7900] text-white font-bold px-3 py-1 text-sm z-10"
                                    style={{ borderRadius: '10px 0 10px 0' }}
                                >
                                    -{discountPercent}%
                                </div>
                            )}

                            <div className="product_meta_box">
                                <div className="product_item__brand_sku flex items-center justify-between">
                                    {p.brands?.nodes && p.brands.nodes.length > 0 ? (
                                        <Link
                                            href={`/product-brands/${p.brands.nodes[0]?.slug || ''}`}
                                            className="product_item__brand"
                                        >
                                            {p.brands.nodes[0]?.name || 'Бренд не указан'}
                                        </Link>
                                    ) : (
                                        <span className="product_item__brand">Бренд не указан</span>
                                    )}
                                    {p.sku && (
                                        <span style={{ margin: '0px !important' }} className="product_item__sku stock_count m-0">
                                            {p.sku}
                                        </span>
                                    )}
                                </div>
                                <div className="line_highlight"></div>
                                <Link href={`/product/${p.slug}`} className="product_item__name">
                                    {p.name}
                                </Link>

                                {/* Отображение цены со скидкой */}
                                <span className="product_item__price">
                                    {p.hasCustomDiscount && isInStock && p.customSalePrice ? (
                                        <div className="flex flex-col flex-wrap md:flex-row md:gap-2 items-center justify-center">
                                            <span className="relative inline-block w-fit text-red-500 md:text-xs text-base after:content-[''] after:absolute after:left-0 after:top-1/2 after:w-full after:h-[1px] after:bg-red-500 after:-translate-y-[300%]">
                                                {formatNumber(p.customSalePrice)}
                                            </span>

                                            <span className="">
                                                {formattedPrice}
                                                <span className="text-xs ml-1">🔥</span>
                                            </span>
                                        </div>
                                    ) : (
                                        formattedPrice
                                    )}
                                </span>

                                {/* Отображаем метки товара */}
                                {p.tags && p.tags.length > 0 && (
                                    <div className="product_tags mt-2">
                                        {/* Можно раскомментировать при необходимости */}
                                    </div>
                                )}
                            </div>

                            <MiniCartProvider>
                                <AddToCartButtonInList
                                    productId={btoa(`post:${p.id}`)}
                                    productName={p.name}
                                    productImage={p.image?.sourceUrl ?? '/images/products/default.jpg'}
                                    productPrice={priceNumber}
                                    maxQuantity={p.stockQuantity || 0}
                                />
                            </MiniCartProvider>
                        </div>
                    );
                })}
            </div>

            <div className="shop_actions">
                {currentLoadedPage < initialTotalPages && (
                    <div className="load_more_container">
                        <button
                            onClick={loadMore}
                            disabled={isLoadingMore}
                            className="load_more_button text-black bg-[#ebebeb] hover:bg-[#ebebeb]"
                        >
                            {isLoadingMore ? 'Загрузка...' : 'Загрузить еще'}
                        </button>
                    </div>
                )}

                <div className="pagination_controls">
                    {initialTotalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={initialTotalPages}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}